import {
  FC,
  Suspense,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";
import { Helmet } from "react-helmet";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";
import { Tooltip } from "react-tooltip";
import { graphql } from "relay-runtime";
import {
  createSortContext,
  i18n,
  Input,
  Loader,
  Pagination,
  SortDir,
  useQueryParam,
  useQueryParams,
} from "rujira.ui";
import {
  Check,
  Minus,
  MinusCircle,
  Plus,
} from "rujira.ui/src/components/icons/Icons";
import {
  StrategiesQuery,
  StrategiesQuery$data,
  StrategySortBy,
} from "./__generated__/StrategiesQuery.graphql";
import { Balances, BalancesFallback } from "./components/Balances";
import { BowPoolXykRow } from "./components/BowPoolXyk";
import { GhostVaultRow } from "./components/GhostVault";
import { IndexVaultRow } from "./components/IndexVault";
import { PerpsPoolRow } from "./components/PerpsPool";
import { StakingPoolRow } from "./components/StakingPool";
import { ThorchainPoolRow } from "./components/ThorchainPool";

const Meta = () => {
  return (
    <Helmet>
      <title>Rujira Strategies</title>
      <meta
        name="description"
        content="Multiple ways to make your tokens grow, by providing liquidity across the Rujira products"
      />
      <meta
        name="og:description"
        content="Multiple ways to make your tokens grow, by providing liquidity across the Rujira products"
      />
      <meta
        name="twitter:description"
        content="Multiple ways to make your tokens grow, by providing liquidity across the Rujira products"
      />
    </Helmet>
  );
};

const query = graphql`
  query StrategiesQuery(
    $first: Int
    $last: Int
    $query: String
    $typenames: [String!]
    $after: String
    $before: String
    $sortBy: StrategySortBy
    $sortDir: SortDir
  ) {
    strategies(
      first: $first
      last: $last
      query: $query
      typenames: $typenames
      sortBy: $sortBy
      sortDir: $sortDir
      after: $after
      before: $before
    ) {
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
      edges {
        cursor
        node {
          __typename
          ... on BowPoolXyk {
            ...BowPoolXykRowFragment
          }
          ... on PerpsPool {
            ...PerpsPoolRowFragment
          }
          ... on ThorchainPool {
            ...ThorchainPoolRowFragment
          }
          ... on IndexVault {
            ...IndexVaultRowFragment
          }
          ... on StakingPool {
            ...StakingPoolRowFragment
          }
          ... on GhostVault {
            ...GhostVaultRowFragment
          }
        }
      }
    }
  }
`;

const sort = createSortContext<StrategySortBy>("TVL", SortDir.Desc);

export const Strategies: FC = () => {
  return (
    <sort.Provider>
      <Meta />
      <Content />
    </sort.Provider>
  );
};

export const options = [
  { label: "AMM - XYK", value: "BowPoolXyk" },
  { label: "AMM - THORChain CLP", value: "ThorchainPool" },
  // { label: "Index", value: "IndexVault" },
  { label: "Lending", value: "GhostVault" },
  { label: "Perps LP / xLP", value: "PerpsPool" },
  { label: "Staking", value: "StakingPool" },
];

const Content: FC = () => {
  const [filters_, setFilters_] = useQueryParam<string>(
    "filters",
    options
      .filter((x) => x.value !== "ThorchainPool")
      .map((x) => x.value)
      .join(",")
  );

  const filters = useMemo(
    () => (filters_ ? filters_.split(",") : []),
    [filters_]
  );
  const setFilters = (f: string[]) => setFilters_(f.join(","));
  const [params, setParams] = useQueryParams<string>({
    after: "",
    before: "",
    search: "",
  });
  const { search, after, before } = params;

  const resetFilters = () => {
    //setFilters([]);
    setFilters(options.map((x) => x.value));
  };

  const setFilter = (filter: string) => {
    setFilters([filter]);
  };

  const toggleFilter = (filter: string) => {
    setFilters(
      filters.includes(filter)
        ? filters.filter((x) => x !== filter)
        : [...filters, filter]
    );
  };

  const [q, loadQuery] = useQueryLoader<StrategiesQuery>(query);
  const [isLoading, transition] = useTransition();
  const { sortBy, sortDir } = sort.useValues();

  useEffect(() => {
    transition(() => {
      loadQuery(
        {
          [before ? "last" : "first"]: 20,
          query: search || undefined,
          typenames: filters.length ? filters : undefined,
          sortBy,
          sortDir,
          after: after || undefined,
          before: before || undefined,
        },
        { fetchPolicy: "store-and-network" }
      );
    });
  }, [search, filters, sortBy, sortDir, after, before]);

  return (
    <>
      <div className="rujira__main rujira__main--gradient pools">
        <div className="rujira__inner--pad">
          <div className="pools__container">
            <div className="pools__left" />
            <div className="pools__main">
              <i18n.h1 className="h1">Strategies</i18n.h1>

              <i18n.h2 className="fs-24 lh-32 fw-400 color-white">
                Multiple ways to use your favorite tokens across the Rujira
                products and generate yield.
              </i18n.h2>

              <div className="flex wrap ai-c mb-2 mt-2">
                <i18n.h4 className="h4 fs-12 mb-0 mr-1 color-grey">
                  Quick Filters
                </i18n.h4>
                <nav className="tabs tabs--sm wrap w-full">
                  <i18n.a
                    className={filters.length === 0 ? "selected" : ""}
                    onClick={resetFilters}>
                    All
                  </i18n.a>
                  <div className="divider mx-1" />
                  {options.map((o) => (
                    <Option
                      key={o.value}
                      label={o.label}
                      value={o.value}
                      selected={!filters.length || filters.includes(o.value)}
                      toggleFilter={toggleFilter}
                      setFilter={setFilter}
                    />
                  ))}
                  {isLoading && <Loader className="w-4 h-4" />}
                </nav>
              </div>
              <Input
                label="Search"
                value={search}
                className="mt-1 mb-2"
                onChange={(e) =>
                  setParams({
                    before: "",
                    after: "",
                    search: e.currentTarget.value.toLowerCase(),
                  })
                }>
                {search !== "" && (
                  <MinusCircle
                    className="input-container__clear"
                    onClick={() =>
                      setParams({ before: "", after: "", search: "" })
                    }
                  />
                )}
              </Input>

              <div className="relative card p-3">
                {q ? (
                  <Table
                    q={q}
                    setAfter={(v) => {
                      setParams({ after: v, before: "", search });
                    }}
                    setBefore={(v) => {
                      setParams({ before: v, after: "", search });
                    }}
                  />
                ) : (
                  <Fallback />
                )}
              </div>
            </div>
            <Suspense fallback={<BalancesFallback />}>
              <Balances />
            </Suspense>
          </div>
        </div>
      </div>
      <Tooltip id="pools-tip" className="tooltip" float={true} />
    </>
  );
};

const Option = ({
  label,
  value,
  selected,
  toggleFilter,
  setFilter,
}: {
  label: string;
  value: string;
  selected: boolean;
  toggleFilter: (value: string) => void;
  setFilter: (value: string) => void;
}) => {
  const [hover, setHover] = useState(false);
  return (
    <a onClick={() => setFilter(value)} className={selected ? "selected" : ""}>
      {label}
      {selected ? (
        <button
          onMouseOver={() => setHover(true)}
          onMouseOut={() => setHover(false)}
          className="transparent color-grey hover-white pointer"
          onClick={(e) => {
            e.stopPropagation();
            setHover(false);
            toggleFilter(value);
          }}>
          {hover ? <Minus /> : <Check />}
        </button>
      ) : (
        <button
          className="transparent color-grey hover-white pointer"
          onClick={(e) => {
            e.stopPropagation();
            toggleFilter(value);
          }}>
          <Plus />
        </button>
      )}
    </a>
  );
};

const Table: FC<{
  q: PreloadedQuery<StrategiesQuery>;
  setAfter: (v: string) => void;
  setBefore: (v: string) => void;
}> = ({ q, setAfter, setBefore }) => {
  const data = usePreloadedQuery(query, q);

  return (
    <div className="table-sticky">
      <table className="table table--big condensed mb-2">
        <thead>
          <tr>
            <sort.Item label="Strategy" value="NAME" />
            <sort.Item label="TVL" value="TVL" />
            <i18n.th className="text-right">APR</i18n.th>
            <i18n.th className="text-right">Earn Mechanism</i18n.th>
            <th />
          </tr>
        </thead>
        <tbody>
          {data?.strategies.edges?.map((x) =>
            x?.node ? <Row key={x.cursor} node={x.node} /> : null
          )}
        </tbody>
      </table>
      <Pagination
        pageInfo={data.strategies.pageInfo}
        setAfter={setAfter}
        setBefore={setBefore}
      />
    </div>
  );
};

type Edge = NonNullable<
  NonNullable<StrategiesQuery$data["strategies"]["edges"]>[number]
>;
type Node = NonNullable<Edge["node"]>;

const Row: FC<{ node: Node }> = ({ node }) => {
  switch (node.__typename) {
    case "BowPoolXyk":
      return <BowPoolXykRow k={node} />;
    case "PerpsPool":
      return <PerpsPoolRow k={node} />;
    case "ThorchainPool":
      return <ThorchainPoolRow k={node} />;
    case "IndexVault":
      return <IndexVaultRow k={node} />;
    case "StakingPool":
      return <StakingPoolRow k={node} />;
    case "GhostVault":
      return <GhostVaultRow k={node} />;
  }
};

const Fallback: FC = () => {
  return (
    <div className="table-sticky">
      <table className="table table--big condensed">
        <thead>
          <tr>
            <i18n.th>Deposit</i18n.th>
            <i18n.th>TVL</i18n.th>
            <i18n.th className="text-right">APR</i18n.th>
            <i18n.th className="text-right">Earn Mechanism</i18n.th>
            <th />
          </tr>
        </thead>
        <tbody>
          {[...Array(5)].map((_, i) => (
            <tr key={`skeleton_${i}`}>
              <td>
                <div className="flex ai-c">
                  <div className="lp">
                    <div className="icon-denom skeleton br-2" />
                    <div className="icon-denom skeleton br-2" />
                  </div>
                  <div className="ml-1 w-full skeleton h-1.5 w-1.5 br-2" />
                </div>
              </td>
              <td>
                <div className="w-full skeleton h-1.5 w-1.5 br-2" />
              </td>
              <td>
                <div className="w-full skeleton h-1.5 w-1.5 br-2" />
              </td>
              <td>
                <div className="w-full skeleton h-1.5 w-1.5 br-2" />
              </td>
              <td />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
